import type { EmergencyCall } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import { setSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import type { SinatraState } from '@rsos/sinatra';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import { fetchLocation, removeNumFromQueue } from '../../../../../../actions';
import { FETCH_LOCATION_SUCCESS } from '../../../../../../actions/actionTypes';
import type { RootState } from '../../../../../../app';
import callQueueSelectorNext from '../../../../../../selectors/callQueueSelectorNext';
import { getProductToken } from '../../../../../../utils/getProductToken';
import { isCallEntryActive, parseLeiTerm } from '../../useLeiSearch';
import { registerCommand } from '../registry';
import type {
  ActionCommandDef,
  ArgSuggestion,
  CommandContext,
  CommandResult,
  ParsedCommand,
} from '../types';

const onlyDigits = (s: string): string => s.replace(/\D/g, '');

const MAX_ARG_SUGGESTIONS = 8;

type CallQueue = Record<string, EmergencyCall>;

/**
 * Pull a useful sublabel out of an `EmergencyCall`. Caller ID alone is
 * already in the row's primary label, so the sublabel surfaces what
 * the dispatcher is most likely to recognize: street address, then
 * fall back to a status hint, then nothing. Mirrors the priority
 * `useGroupedResults` uses for call-result sublabels.
 */
function callSublabel(call: EmergencyCall | undefined): string | undefined {
  if (!call) return undefined;
  const primaryLocation = call.core?.locations?.[0];
  const rgAddress =
    primaryLocation && 'rgAddress' in primaryLocation
      ? primaryLocation.rgAddress
      : undefined;
  if (rgAddress) return rgAddress;
  const status = call.callStatuses?.[0];
  if (status) return status;
  return undefined;
}

/**
 * Resolve the user's typed arg to a `callerID` from the active call
 * queue. The queue is keyed by callerID (which is the phone number),
 * so we match against:
 *   1. exact key hit (raw input)
 *   2. digit-only substring hit against any queue key (so the user
 *      can type `5551234` and find a `+15551234567` entry)
 *
 * Returns the matching key, or `undefined` if nothing in the queue
 * matches — the command then reports `not_found` to the host.
 */
function resolveCallerID(
  arg: string,
  callQueue: CallQueue | undefined,
): string | undefined {
  if (!arg || !callQueue) return undefined;

  if (callQueue[arg]) return arg;

  const digits = onlyDigits(arg);
  if (digits.length === 0) return undefined;

  const keys = Object.keys(callQueue);

  // Prefer the longest-suffix match so that a partial like `1234`
  // that happens to be a substring of multiple call keys still
  // resolves deterministically (newest queue insertion wins ties
  // because `Object.keys` preserves insertion order).
  let best: string | undefined;
  for (const key of keys) {
    const keyDigits = onlyDigits(key);
    if (!keyDigits.includes(digits)) continue;
    if (!best || keyDigits.length > onlyDigits(best).length) best = key;
  }
  return best;
}

/**
 * Live filter for `/call` autocomplete rows. Empty arg returns the
 * full active queue (capped); a typed arg filters down to entries
 * whose callerID digits *include* the typed digits, so `/call 88`
 * surfaces every active call whose number contains `88` (start,
 * middle, or end).
 *
 * The relaxed substring match (vs strict prefix) matches how the
 * existing `runCallsSearch` filters results today, so a user toggling
 * between the slash command and the regular search bar gets the same
 * mental model.
 */
function getCallSuggestions(
  parsed: ParsedCommand,
  ctx: CommandContext,
): ArgSuggestion[] {
  // Source of truth for the active call queue in the new map search
  // experience — ANI/ALI-permission-filtered and merged with outbound
  // text-from-911 conversations. Reading the raw
  // `state.emergencyCalls.callQueue` would surface entries the user
  // shouldn't see / miss text-only conversations the rest of the JV
  // already shows.
  const callQueue = callQueueSelectorNext(
    ctx.getState() as RootState,
  ) as unknown as CallQueue | undefined;
  if (!callQueue) return [];

  const queryRaw = parsed.rest.trim();
  const queryLower = queryRaw.toLowerCase();
  const queryDigits = onlyDigits(queryRaw);

  const matches: ArgSuggestion[] = [];
  for (const [callerId, call] of Object.entries(callQueue)) {
    if (queryRaw) {
      const keyDigits = onlyDigits(callerId);
      const digitHit =
        queryDigits.length > 0 && keyDigits.includes(queryDigits);
      const textHit = callerId.toLowerCase().includes(queryLower);
      if (!digitHit && !textHit) continue;
    }
    matches.push({
      id: callerId,
      value: callerId,
      label: formatInternationalPhoneNumberHuman(callerId) || callerId,
      sublabel: callSublabel(call),
    });
    if (matches.length >= MAX_ARG_SUGGESTIONS) break;
  }
  return matches;
}

// `fetchLocation` is a JS thunk; the action returned to `dispatch`
// resolves to `{ action, requestID, payloadID }` on success and to
// the `fetchLocationFailed` action on failure. The shape isn't
// expressible against the JS thunk signature so we narrow with a
// boundary cast at the dispatch site.
type LegacyDispatch = (action: unknown) => Promise<
  | {
      action?: { type?: string };
    }
  | undefined
>;

const callCommand: ActionCommandDef = {
  id: 'call',
  kind: 'action',
  title: 'Open call',
  description:
    'Open the call pane for an active call by phone number or caller id.',
  usage: '<phone number>',
  getArgSuggestions: getCallSuggestions,
  execute: async (
    parsed: ParsedCommand,
    ctx: CommandContext,
  ): Promise<CommandResult> => {
    const arg = parsed.rest;
    if (!arg) {
      return {
        ok: false,
        reason: 'invalid_args',
        message: 'Usage: /call <phone number>',
      };
    }

    const callQueue = callQueueSelectorNext(
      ctx.getState() as RootState,
    ) as unknown as CallQueue | undefined;

    const callerID = resolveCallerID(arg, callQueue);
    if (callerID) {
      // Selecting the caller is the same hook the queue itself uses
      // to open the call pane (mirrors
      // `useResultActions.handleCallClick`). No pan / highlight from
      // here — the queue's own effects fan that out off
      // `selectedCallerID`.
      ctx.dispatch(setSelectedCallerID(callerID));
      return { ok: true };
    }

    // No active-queue match. Fall back to the legacy LEI location
    // pull when the arg parses as a USA phone number — same flow
    // `RegularSearchExperience.triggerSearch` runs for plain
    // submits. `parseLeiTerm` widens 10-digit input to the leading-
    // `1` 11-digit form before the fetch fires; what the user typed
    // in the bar is left alone (the slash command host clears the
    // input on a successful commit anyway).
    const leiCallerID = parseLeiTerm(arg);
    if (!leiCallerID) {
      return {
        ok: false,
        reason: 'not_found',
        message: `No active call matches "${arg}".`,
      };
    }

    const stateWithSinatra = ctx.getState() as RootState & {
      sinatra: SinatraState;
    };
    const { productToken } = getProductToken(stateWithSinatra.sinatra);
    const token =
      productToken ||
      (typeof window !== 'undefined'
        ? (window.localStorage?.getItem('productToken') ?? '')
        : '');
    if (!token) {
      return {
        ok: false,
        reason: 'unauthorized',
        message: 'No product token available for LEI lookup.',
      };
    }

    try {
      const dispatch = ctx.dispatch as unknown as LegacyDispatch;
      const res = await dispatch(
        fetchLocation(
          leiCallerID,
          token,
        ) as unknown as Parameters<LegacyDispatch>[0],
      );
      if (res?.action?.type !== FETCH_LOCATION_SUCCESS) {
        return {
          ok: false,
          reason: 'not_found',
          message: `No location found for "${arg}".`,
        };
      }
      // `fetchLocation` unconditionally seeds the call queue with an
      // entry for `leiCallerID` even when the LEI service returned
      // nothing usable. Re-check the freshly-populated queue entry
      // against `isCallEntryActive` (primary/secondary location or an
      // active conversation) and undo the placeholder add when it's
      // an empty shell — otherwise it would surface as a phantom
      // "Active Call" row the next time the user re-runs the same
      // query from recents.
      const postQueue = callQueueSelectorNext(
        ctx.getState() as RootState,
      ) as unknown as CallQueue | undefined;
      const queueEntry = postQueue?.[leiCallerID];
      if (!isCallEntryActive(queueEntry)) {
        ctx.dispatch(removeNumFromQueue(leiCallerID));
        return {
          ok: false,
          reason: 'not_found',
          message: `No location found for "${arg}".`,
        };
      }
      ctx.dispatch(setSelectedCallerID(leiCallerID));
      return { ok: true };
    } catch {
      return {
        ok: false,
        reason: 'runtime',
        message: `Lookup failed for "${arg}".`,
      };
    }
  },
};

registerCommand(callCommand);

export default callCommand;
